import {
  MinusOutlined,
  PlusOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { Collapse, Space, Tooltip } from 'antd';
import { FC, useState } from 'react';
import * as THREE from 'three';

import { ReactComponent as CollapseIcon } from '../../../../../assets/svgs/collapse.svg';

import FixtureItem from './components/FixtureItem/FixtureItem';
import FixturePropertiesPanel from './components/FixtureProperties';
import Settings from './components/Settings/Settings';
import ShopperProperties from './components/ShopperProperties/ShopperProperties';

import {
  FixtureItem as FixtureItemType,
  QueueLineModel,
  ShopperMesh,
} from '../../types';

import { fixtures } from '../../fixtures';
import './SidePanel.less';
import { formatCategoryName } from './utils';

const { Panel } = Collapse;

interface SidePanelProps {
  onDragFixture: (item: FixtureItemType) => void;
  selectedMeshes?: THREE.Mesh[];
  onUpdateMesh?: (mesh: THREE.Mesh, updates: any) => void;
  selectedQueueLine?: QueueLineModel;
  onUpdateShopper?: (
    shopper: ShopperMesh,
    action: 'updateShopperType' | 'removeShopper' | 'updateElapsedWaitTime',
    value?: any
  ) => void;
  overlappingShoppers?: string[];
  isGridVisible: boolean;
  isRendered: boolean;
  onToggleGrid: () => void;
  onToggleRenderType: () => void;
  cubeSize: { width: number; height: number; depth: number };
  sphereRadius: number;
  handleUpdateCubeSize: (
    mesh: THREE.Mesh,
    field: 'width' | 'height' | 'depth',
    value: string
  ) => void;
  handleUpdateSphereRadius: (mesh: THREE.Mesh, value: string) => void;
  handleUpdateColor: (mesh: THREE.Mesh, color: string) => void;
}

type FixtureCategoryMap = { [category: string]: FixtureItemType[] };

const SidePanel: FC<SidePanelProps> = ({
  onDragFixture,
  selectedMeshes = [],
  selectedQueueLine,
  onUpdateShopper,
  overlappingShoppers = [],
  isGridVisible,
  isRendered,
  onToggleGrid,
  onToggleRenderType,
  cubeSize,
  sphereRadius,
  handleUpdateCubeSize,
  handleUpdateSphereRadius,
  handleUpdateColor,
}) => {
  const [showFixtures, setShowFixtures] = useState<boolean>(true);
  const [showProperties, setShowProperties] = useState<boolean>(true);
  const [panelCollapsed, setPanelCollapsed] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);

  const toggleShowFixtures = () => {
    if ('startViewTransition' in document) {
      (document as any).startViewTransition(() => {
        setShowFixtures(!showFixtures);
      });
    } else {
      setShowFixtures(!showFixtures);
    }
  };

  const toggleShowProperties = () => {
    if ('startViewTransition' in document) {
      (document as any).startViewTransition(() => {
        setShowProperties(!showProperties);
      });
    } else {
      setShowProperties(!showProperties);
    }
  };

  const togglePanelCollapse = () => {
    if ('startViewTransition' in document) {
      (document as any).startViewTransition(() => {
        setPanelCollapsed(!panelCollapsed);
      });
    } else {
      setPanelCollapsed(!panelCollapsed);
    }
  };

  const toggleShowSettings = () => {
    if ('startViewTransition' in document) {
      (document as any).startViewTransition(() => {
        setShowSettings(!showSettings);
      });
    } else {
      setShowSettings(!showSettings);
    }
  };

  const fixturesByCategory: FixtureCategoryMap = fixtures.reduce(
    (acc, fixture) => {
      const category = fixture.category || 'other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(fixture);
      return acc;
    },
    {} as FixtureCategoryMap
  );

  return (
    <div className={`feb-side-panel ${panelCollapsed && 'collapsed'}`}>
      {panelCollapsed ? (
        <div className="collapsed-panel-control">
          <Tooltip title="Expand side panel" placement="left">
            <div className="collapse-button" onClick={togglePanelCollapse}>
              <CollapseIcon
                className="collapse-icon rotated"
                width={24}
                height={24}
              />
            </div>
          </Tooltip>
        </div>
      ) : (
        <>
          <div
            className={`side-panel-header ${
              showFixtures ? 'visible' : 'hidden'
            }`}
          >
            <div className="side-panel-header__section-left">
              <h1>Fixtures</h1>
            </div>
            <div className="side-panel-header__section-right">
              <Space direction="horizontal" size="middle">
                <Tooltip title="Settings" mouseEnterDelay={0.5}>
                  <SettingOutlined
                    className="side-panel-icon"
                    onClick={toggleShowSettings}
                  />
                </Tooltip>
                {showFixtures ? (
                  <Tooltip title="Hide" mouseEnterDelay={0.5}>
                    <MinusOutlined
                      onClick={toggleShowFixtures}
                      className="side-panel-icon"
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title="Show" mouseEnterDelay={0.5}>
                    <PlusOutlined
                      onClick={toggleShowFixtures}
                      className="side-panel-icon"
                    />
                  </Tooltip>
                )}
                <Tooltip title="Collapse side panel" mouseEnterDelay={0.5}>
                  <div className="collapse-button-wrapper">
                    <CollapseIcon
                      onClick={togglePanelCollapse}
                      className="collapse-icon side-panel-icon"
                      width={24}
                      height={24}
                    />
                  </div>
                </Tooltip>
              </Space>
            </div>
          </div>

          <div
            className={`fixtures-wrapper ${
              showFixtures ? 'visible' : 'hidden'
            }`}
          >
            <Collapse ghost defaultActiveKey={['0']}>
              {Object.entries(fixturesByCategory)
                .sort(([categoryA], [categoryB]) => {
                  if (categoryA === 'Demo') return -1;
                  if (categoryB === 'Demo') return 1;
                  return 0;
                })
                .map(([category, items], index) => (
                  <Panel
                    header={formatCategoryName(category)}
                    key={index.toString()}
                    style={{ textTransform: 'capitalize' }}
                  >
                    <div className="fixture-items-wrapper">
                      {items.map((item) => (
                        <FixtureItem
                          key={item.id}
                          item={item}
                          onDragFixture={onDragFixture}
                        />
                      ))}
                    </div>
                  </Panel>
                ))}
            </Collapse>
          </div>

          {!(
            selectedMeshes.length === 1 &&
            selectedMeshes[0].userData.isQueueLineShopper
          ) && (
            <FixturePropertiesPanel
              showProperties={showProperties}
              toggleShowProperties={toggleShowProperties}
              selectedMeshes={selectedMeshes}
              cubeSize={cubeSize}
              sphereRadius={sphereRadius}
              handleUpdateCubeSize={handleUpdateCubeSize}
              handleUpdateSphereRadius={handleUpdateSphereRadius}
              handleUpdateColor={handleUpdateColor}
            />
          )}

          {selectedMeshes.length === 1 &&
            selectedMeshes[0].userData.isQueueLineShopper && (
              <ShopperProperties
                shopper={selectedMeshes[0] as ShopperMesh}
                selectedQueueLine={selectedQueueLine}
                overlappingShoppers={overlappingShoppers}
                onUpdateShopper={onUpdateShopper || (() => {})}
              />
            )}

          {showSettings && (
            <Settings
              isGridVisible={isGridVisible}
              isRendered={isRendered}
              onToggleGrid={onToggleGrid}
              onToggleRenderType={onToggleRenderType}
              onClose={toggleShowSettings}
            />
          )}
        </>
      )}
    </div>
  );
};

export default SidePanel;
